import { useEffect, useState, useCallback } from "react";
import Icon from "@/components/Icon";
import {
  crmList, crmStats, crmReminders, crmDetail, crmPatch,
  crmAddNote, crmDeleteNote, crmAddReminder, crmToggleReminder, crmDeleteReminder,
} from "@/lib/api";

const STAGES = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "qualified", label: "Qualified" },
  { key: "proposal", label: "Proposal" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
];

function fmtDate(ts) {
  if (!ts) return "";
  try { return new Date(ts).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ts; }
}
function isOverdue(ts) { try { return new Date(ts).getTime() < Date.now(); } catch { return false; } }

export default function CrmView() {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ by_stage: {}, reminders_due: 0, reminders_upcoming: 0 });
  const [reminders, setReminders] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [remTitle, setRemTitle] = useState("");
  const [remDue, setRemDue] = useState("");

  const load = useCallback(async () => {
    try {
      const [c, s, r] = await Promise.all([crmList(q ? { q } : {}), crmStats(), crmReminders()]);
      setContacts(c); setStats(s); setReminders(r);
    } catch { /* ignore */ }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const open = async (id) => {
    setSelected(id); setDetail(null); setNote(""); setRemTitle(""); setRemDue("");
    try { setDetail(await crmDetail(id)); } catch { /* ignore */ }
  };
  const close = () => { setSelected(null); setDetail(null); };

  const refresh = async (id) => {
    const d = await crmDetail(id); setDetail(d);
    setContacts((prev) => prev.map((c) => (c.id === id ? d.lead : c)));
    crmStats().then(setStats); crmReminders().then(setReminders);
  };

  const setStage = async (id, stage) => {
    const lead = await crmPatch(id, { crm_stage: stage });
    setContacts((prev) => prev.map((c) => (c.id === id ? lead : c)));
    setDetail((prev) => (prev ? { ...prev, lead } : prev));
    crmStats().then(setStats);
  };
  const setOwner = async (id, owner) => {
    const lead = await crmPatch(id, { crm_owner: owner });
    setDetail((prev) => (prev ? { ...prev, lead } : prev));
    setContacts((prev) => prev.map((c) => (c.id === id ? lead : c)));
  };
  const addNote = async () => {
    if (!note.trim() || !selected) return;
    await crmAddNote(selected, note.trim()); setNote(""); refresh(selected);
  };
  const delNote = async (nid) => { await crmDeleteNote(selected, nid); refresh(selected); };
  const addRem = async () => {
    if (!remTitle.trim() || !remDue || !selected) return;
    const iso = new Date(remDue).toISOString();
    await crmAddReminder(selected, remTitle.trim(), iso); setRemTitle(""); setRemDue(""); refresh(selected);
  };
  const toggleRem = async (rid, done) => { await crmToggleReminder(rid, done); refresh(selected); };
  const delRem = async (rid) => { await crmDeleteReminder(rid); refresh(selected); };

  const lead = detail?.lead;

  return (
    <div className="crm" data-testid="crm-view">
      <div className="crm-bar">
        <div className="crm-search">
          <Icon name="Search" size={16} />
          <input placeholder="Search contacts by name, email or organization…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="crm-search" />
        </div>
        <div className="crm-remind" data-testid="crm-reminders-chip">
          <Icon name="Sparkles" size={15} />
          <span><strong>{stats.reminders_due}</strong> due · {stats.reminders_upcoming} upcoming</span>
        </div>
      </div>

      <div className="crm-board">
        {STAGES.map((st) => {
          const items = contacts.filter((c) => (c.crm_stage || "new") === st.key);
          return (
            <div className={`crm-col crm-col--${st.key}`} key={st.key} data-testid={`crm-col-${st.key}`}>
              <div className="crm-col__head"><span>{st.label}</span><b>{items.length}</b></div>
              <div className="crm-col__body">
                {items.length === 0 && <div className="crm-col__empty">—</div>}
                {items.map((c) => (
                  <button key={c.id} className="crm-card" onClick={() => open(c.id)} data-testid={`crm-card-${c.id}`}>
                    <div className="crm-card__top">
                      <span className="crm-card__name">{c.name}</span>
                      {c.lead_score && <span className={`adm-badge adm-badge--${c.lead_score}`}>{c.lead_score}</span>}
                    </div>
                    <div className="crm-card__org">{c.organization || c.email}</div>
                    {c.crm_owner && <div className="crm-card__owner"><Icon name="User" size={11} /> {c.crm_owner}</div>}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {selected && (
        <div className="crm-drawer" role="dialog" data-testid="crm-drawer">
          <div className="crm-drawer__scrim" onClick={close} />
          <div className="crm-drawer__panel">
            <button className="crm-drawer__close" onClick={close} data-testid="crm-drawer-close"><Icon name="X" size={18} /></button>
            {!lead && <div className="adm-empty">Loading…</div>}
            {lead && (
              <div className="crm-detail">
                <h2 className="crm-detail__name">{lead.name}</h2>
                <div className="crm-detail__contact">
                  <a href={`mailto:${lead.email}`}><Icon name="Mail" size={14} /> {lead.email}</a>
                  {lead.phone && <span><Icon name="Phone" size={14} /> {lead.phone}</span>}
                  {lead.organization && <span><Icon name="Building2" size={14} /> {lead.organization}</span>}
                </div>

                <div className="crm-detail__row">
                  <label className="crm-field">
                    <span>Stage</span>
                    <select value={lead.crm_stage || "new"} onChange={(e) => setStage(lead.id, e.target.value)} data-testid="crm-stage-select">
                      {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                  </label>
                  <label className="crm-field">
                    <span>Owner</span>
                    <input defaultValue={lead.crm_owner || ""} placeholder="Assign owner" onBlur={(e) => setOwner(lead.id, e.target.value)} data-testid="crm-owner-input" />
                  </label>
                </div>

                {lead.ai_summary && (
                  <div className="crm-ai"><Icon name="Brain" size={14} /> {lead.ai_summary}</div>
                )}

                {/* Reminders */}
                <div className="crm-sec">
                  <h3><Icon name="Sparkles" size={14} /> Follow-up Reminders</h3>
                  <div className="crm-remadd">
                    <input placeholder="Reminder title" value={remTitle} onChange={(e) => setRemTitle(e.target.value)} data-testid="crm-reminder-title" />
                    <input type="datetime-local" value={remDue} onChange={(e) => setRemDue(e.target.value)} data-testid="crm-reminder-due" />
                    <button className="adm-btn adm-btn--blue" onClick={addRem} data-testid="crm-reminder-add">Add</button>
                  </div>
                  <ul className="crm-remlist">
                    {(detail.reminders || []).map((r) => (
                      <li key={r.id} className={`crm-rem ${r.done ? "is-done" : ""} ${!r.done && isOverdue(r.due_at) ? "is-over" : ""}`} data-testid={`crm-reminder-${r.id}`}>
                        <button className="crm-rem__check" onClick={() => toggleRem(r.id, !r.done)} title="Toggle done"><Icon name={r.done ? "CheckCircle2" : "Sparkles"} size={16} /></button>
                        <div className="crm-rem__meta"><strong>{r.title}</strong><small>{fmtDate(r.due_at)}{!r.done && isOverdue(r.due_at) ? " · overdue" : ""}</small></div>
                        <button className="crm-rem__del" onClick={() => delRem(r.id)} title="Delete"><Icon name="X" size={14} /></button>
                      </li>
                    ))}
                    {(detail.reminders || []).length === 0 && <li className="crm-empty2">No reminders yet.</li>}
                  </ul>
                </div>

                {/* Notes */}
                <div className="crm-sec">
                  <h3><Icon name="FileText" size={14} /> Notes</h3>
                  <div className="crm-noteadd">
                    <textarea placeholder="Add an internal note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="crm-note-input" />
                    <button className="adm-btn adm-btn--ghost" onClick={addNote} data-testid="crm-note-add"><Icon name="Send" size={14} /> Save note</button>
                  </div>
                  <ul className="crm-notelist">
                    {(detail.notes || []).map((n) => (
                      <li key={n.id} className="crm-note" data-testid={`crm-note-${n.id}`}>
                        <p>{n.text}</p>
                        <div className="crm-note__foot"><small>{n.author} · {fmtDate(n.created_at)}</small><button onClick={() => delNote(n.id)} title="Delete"><Icon name="X" size={12} /></button></div>
                      </li>
                    ))}
                    {(detail.notes || []).length === 0 && <li className="crm-empty2">No notes yet.</li>}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
